import getSlug from 'speakingurl';
import { useReduxState } from 'hooks';
import { subtitleLangCodes } from 'lib/lang';
import { ApiDownloadButton, Button } from 'components/common';
import { Icon } from 'components/common/icons';
import { EditorBlockVideoSubtitlesAdd } from '.';

import type { BlockVideo } from 'shared-types';

type Subtitles = BlockVideo['locales'][string]['subtitles'];

interface Props {
  disabled?: boolean;
  id: string;
  onChange: ({ subtitles }: { subtitles: Subtitles }) => void;
  subtitles: Subtitles;
}

const EditorBlockVideoSubtitles = ({
  disabled,
  id,
  onChange,
  subtitles
}: Props) => {
  const { messages, theme } = useReduxState();

  const handleAdd = ({ subtitleLanguage, subtitleUrl }: Subtitles[number]) => {
    onChange({
      subtitles: [...(subtitles || []), { subtitleLanguage, subtitleUrl }]
    });
  };

  const handleDelete = (subtitleIndex: number) => {
    onChange({
      subtitles: [
        ...subtitles.slice(0, subtitleIndex),
        ...subtitles.slice(subtitleIndex + 1)
      ]
    });
  };

  return (
    <>
      <div className="title">{messages.videoCaptions}</div>

      <div className="subtitles">
        {subtitles?.map((subtitle, subtitleIndex) => (
          <div className="file" key={subtitleIndex}>
            <div className="file-language">
              <div className="file-language-icon">
                <Icon icon={['far', 'file-alt']} />
              </div>
              <div className="file-language-text">
                {subtitleLangCodes[subtitle.subtitleLanguage || 'en']}
              </div>
            </div>

            <div className="file-download">
              <ApiDownloadButton
                category="light"
                external={true}
                icon="download"
                filename={`${getSlug(
                  subtitleLangCodes[subtitle.subtitleLanguage || 'en']
                )}.vtt`}
                size="header"
                tooltip={messages.commonDownloadFile}
                url={subtitle.subtitleUrl}
              />
            </div>

            {!disabled ? (
              <div className="file-delete">
                <Button
                  category="light"
                  icon="trash-alt"
                  onClick={() => handleDelete(subtitleIndex)}
                  size="header"
                  tooltip={messages.commonDelete}
                />
              </div>
            ) : null}
          </div>
        ))}

        {!disabled ? (
          <div className="add">
            <EditorBlockVideoSubtitlesAdd
              id={`${id}-subtitles-add`}
              onSubmit={handleAdd}
            />
          </div>
        ) : null}
      </div>

      <style jsx>{`
        .title {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0 0 7px;
        }
        .title:not(:first-child) {
          margin-top: 20px;
        }
        .file {
          align-items: center;
          display: flex;
          gap: 10px;
          margin-bottom: 10px;
        }
        .file-language {
          background-color: white;
          border-radius: ${theme.inputBorderRadius};
          color: ${theme.textColorLighter};
          font-size: 1.5rem;
          font-weight: 700;
          margin-right: 5px;
          padding: 6px 12px;
          height: 30px;
          width: 180px;
        }
        .file-language-icon {
          display: inline-block;
          font-size: 1.5rem;
          margin-right: 7px;
        }
        .file-language-text {
          display: inline-block;
          font-size: 1.4rem;
          position: relative;
          top: -1px;
        }
      `}</style>
    </>
  );
};

export default EditorBlockVideoSubtitles;
